// frontend/src/pages/Entities/EntityList.jsx
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getEntities, syncEntities, createEntity } from "../../api/entity.api.js";
import {
  PageHeader,
  Spinner,
  EmptyState,
} from "../../components/ui";
import {
  Building2,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Layers,
  Search,
  X,
  Plus,
  Save,
} from "lucide-react";

// ─── Highlight matched text ───────────────────────────────────────────────────

function Highlight({ text, query }) {
  if (!query || !text) return <span>{text}</span>;

  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;

  return (
    <span>
      {text.slice(0, idx)}
      <mark className="bg-brand-100 text-brand-800 rounded px-0.5 not-italic font-semibold">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </span>
  );
}

// ─── Check if entity matches search query ─────────────────────────────────────

function entityMatches(entity, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    entity.name?.toLowerCase().includes(q) ||
    entity.code?.toLowerCase().includes(q)
  );
}

// ─── New entity modal ─────────────────────────────────────────────────────────

function NewEntityModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", code: "" });
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => createEntity({
      name: form.name.trim(),
      code: form.code.trim(),
    }),
    onSuccess: (entity) => {
      onCreated(entity.id);
    },
    onError: (err) => setError(err?.response?.data?.error ?? err.message),
  });

  function set(field) {
    return (e) => {
      const val = field === "code" ? e.target.value.toUpperCase() : e.target.value;
      setForm((f) => ({ ...f, [field]: val }));
    };
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">New Entity</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="label text-xs">Name <span className="text-red-400">*</span></label>
            <input
              className="input text-sm"
              placeholder="PT Rhaya Industri..."
              value={form.name}
              onChange={set("name")}
              autoFocus
            />
          </div>

          <div>
            <label className="label text-xs">
              Code <span className="text-red-400">*</span>
              <span className="text-gray-400 font-normal ml-1">max 10 chars</span>
            </label>
            <input
              className="input text-sm font-mono"
              placeholder="RHIND"
              maxLength={10}
              value={form.code}
              onChange={set("code")}
            />
          </div>

        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={() => { setError(null); save.mutate(); }}
            disabled={save.isPending || !form.name.trim() || !form.code.trim()}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Save size={14} /> {save.isPending ? "Creating..." : "Create Entity"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EntityList() {
  const { isMaster } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showNewModal, setShowNewModal] = useState(false);

  // Track manually opened/closed groups (user intent)
  const [manualOpen, setManualOpen] = useState(new Set());
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);
  const isSearching = search.trim().length > 0;

  const { data: entities, isLoading } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
  });

  const sync = useMutation({
    mutationFn: syncEntities,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["entities"] }),
  });

  // ── Group entities ────────────────────────────────────────────────────────
  const grouped = entities
    ? entities.reduce((acc, e) => {
        const key = e.group?.id ?? "__others__";
        const label = e.group?.name ?? "Others";
        if (!acc[key]) acc[key] = { id: key, name: label, entities: [] };
        acc[key].entities.push(e);
        return acc;
      }, {})
    : {};

  const groupList = Object.values(grouped).sort((a, b) => {
    if (a.id === "__others__") return 1;
    if (b.id === "__others__") return -1;
    return a.name.localeCompare(b.name);
  });

  // ── Filter logic ──────────────────────────────────────────────────────────
  // When searching: each group shows only matching entities
  // Groups with no matches are dimmed and collapsed
  const filteredGroups = groupList.map((group) => ({
    ...group,
    entities: isSearching
      ? group.entities.filter((e) => entityMatches(e, search))
      : group.entities,
    hasMatches: isSearching
      ? group.entities.some((e) => entityMatches(e, search))
      : true,
  }));

  const totalMatches = isSearching
    ? filteredGroups.reduce((sum, g) => sum + g.entities.length, 0)
    : null;

  // ── Open state logic ──────────────────────────────────────────────────────
  // Searching → auto-expand groups with matches
  // Not searching → use manual open state
  function isGroupOpen(group) {
    if (isSearching) return group.hasMatches;
    return manualOpen.has(group.id);
  }

  function toggleGroup(groupId) {
    // Only toggle when not searching
    if (isSearching) return;
    setManualOpen((prev) => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  }

  function clearSearch() {
    setSearch("");
    searchRef.current?.focus();
  }

  const namedGroupCount = groupList.filter((g) => g.id !== "__others__").length;

  return (
    <div>
      {showNewModal && (
        <NewEntityModal
          onClose={() => setShowNewModal(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ["entities"] });
            navigate(`/entities/${id}`);
          }}
        />
      )}

      <PageHeader
        title="Entities"
        subtitle={
          entities
            ? `${entities.length} companies · ${namedGroupCount} groups`
            : ""
        }
        action={
          isMaster && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowNewModal(true)}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                <Plus size={15} /> New Entity
              </button>
              <button
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <RefreshCw
                  size={15}
                  className={sync.isPending ? "animate-spin" : ""}
                />
                {sync.isPending ? "Syncing..." : "Sync from HR"}
              </button>
            </div>
          )
        }
      />

      {sync.isError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Sync failed: {sync.error?.message}
        </div>
      )}

      {/* ── Search bar ───────────────────────────────────────────────────── */}
      <div className="mb-5">
        <div className="relative max-w-sm">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            ref={searchRef}
            className="input pl-9 pr-8 text-sm"
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isSearching && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Search result count */}
        {isSearching && (
          <p className="mt-2 text-xs text-gray-500">
            {totalMatches === 0 ? (
              <span className="text-amber-600">
                No entities found for "{search}"
              </span>
            ) : (
              <>
                <span className="font-medium text-gray-700">
                  {totalMatches}
                </span>{" "}
                {totalMatches === 1 ? "entity" : "entities"} matching{" "}
                <span className="font-medium text-gray-700">"{search}"</span>
              </>
            )}
          </p>
        )}
      </div>

      {/* ── Entity list ───────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : !groupList.length ? (
        <EmptyState
          title="No entities found"
          description="Sync from HR to load your entities."
          action={
            isMaster && (
              <button
                onClick={() => sync.mutate()}
                className="btn-primary text-sm"
              >
                Sync now
              </button>
            )
          }
        />
      ) : isSearching && totalMatches === 0 ? (
        <EmptyState
          title="No matching entities"
          description={`No entities match "${search}". Try a different name or code.`}
          action={
            <button
              onClick={clearSearch}
              className="btn-secondary text-sm flex items-center gap-1.5"
            >
              <X size={13} /> Clear search
            </button>
          }
        />
      ) : (
        <div className="space-y-2">
          {filteredGroups.map((group) => {
            const isOpen = isGroupOpen(group);
            const isOthers = group.id === "__others__";
            const isDimmed = isSearching && !group.hasMatches;

            return (
              <div
                key={group.id}
                className={`card overflow-hidden transition-opacity duration-200 ${
                  isDimmed ? "opacity-40" : "opacity-100"
                }`}
              >
                {/* ── Group header ─────────────────────────────────────── */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  disabled={isSearching}
                  className={`w-full flex items-center justify-between px-6 py-4 transition-colors text-left ${
                    isSearching
                      ? "cursor-default"
                      : "hover:bg-gray-50 cursor-pointer"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isOthers ? "bg-gray-100" : "bg-brand-50"
                      }`}
                    >
                      <Layers
                        size={17}
                        className={
                          isOthers ? "text-gray-400" : "text-brand-500"
                        }
                      />
                    </div>
                    <div>
                      <p
                        className={`text-sm font-semibold ${
                          isOthers ? "text-gray-400 italic" : "text-gray-900"
                        }`}
                      >
                        {group.name}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {isSearching && group.hasMatches ? (
                          <>
                            <span className="text-brand-600 font-medium">
                              {group.entities.length}
                            </span>{" "}
                            match{group.entities.length !== 1 ? "es" : ""}
                            {" · "}
                            {grouped[group.id]?.entities.length} total
                          </>
                        ) : (
                          <>
                            {group.entities.length}{" "}
                            {group.entities.length === 1
                              ? "entity"
                              : "entities"}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  {/* Hide chevron when searching — state is driven by search */}
                  {!isSearching &&
                    (isOpen ? (
                      <ChevronDown
                        size={16}
                        className="text-gray-400 flex-shrink-0"
                      />
                    ) : (
                      <ChevronRight
                        size={16}
                        className="text-gray-400 flex-shrink-0"
                      />
                    ))}
                </button>

                {/* ── Entity rows ───────────────────────────────────────── */}
                {isOpen && group.entities.length > 0 && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50 bg-gray-50/40">
                    {group.entities.map((e) => (
                      <Link
                        key={e.id}
                        to={`/entities/${e.id}`}
                        className="flex items-center justify-between px-6 py-3 pl-16 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                            <Building2 size={14} className="text-gray-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              <Highlight
                                text={e.name}
                                query={isSearching ? search : ""}
                              />
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {e.brand && (
                                <span
                                  className="inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded"
                                  style={{
                                    backgroundColor: e.brand.color
                                      ? `${e.brand.color}20`
                                      : "#f3f4f6",
                                    color: e.brand.color ?? "#6b7280",
                                  }}
                                >
                                  {e.brand.name}
                                </span>
                              )}
                              <span className="text-xs text-gray-400">
                                {e.code && (
                                  <span className="font-mono mr-1.5">
                                    <Highlight
                                      text={e.code}
                                      query={isSearching ? search : ""}
                                    />
                                  </span>
                                )}
                                {e.employeeCount} employees
                                {" · "}
                                {e._count?.agreements ?? 0} active agreement
                                {e._count?.agreements !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!e.isActive && (
                            <span className="badge bg-gray-100 text-gray-400 text-xs">
                              Inactive
                            </span>
                          )}
                          <ChevronRight size={14} className="text-gray-300" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
